import React from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';

import { MODAL_TYPES } from './constants';

const ANIMATION_NAME_BY_TYPE = {
  [MODAL_TYPES.TFE_LAUNCH]: 'modalFromTop 1s ease',
  [MODAL_TYPES.EREADER_LAUNCH]: 'modalFromBottom 1.25s ease',
  [MODAL_TYPES.VOCAB_LAUNCH]: 'modalFromBottom 1.5s ease',
  [MODAL_TYPES.NOTIFICATION]: 'notificationFromBottom 0.2s',
  [MODAL_TYPES.GAME_SELECTOR]: 'popY 0.3s ease-out',
  [MODAL_TYPES.FLUENCY_LAUNCH]: 'popY 0.3s ease-out',
};

const AnimatedModal = ({ shouldFadeOut, children, withBackground, type }) => {
  const animation = ANIMATION_NAME_BY_TYPE[type] || 'smoothPop .7s cubic-bezier(0.1, 1.4, 0.58, 1)';
  const [, duration] = animation.split(' ');
  // if duration is more than 0.7s, set it to 0.5s
  const durationInSeconds = parseFloat(duration) > 0.7 ? '0.5s' : duration;
  return (
    <FadeOutContainer shouldFadeOut={shouldFadeOut}>
      {withBackground && <BackgroundOverlay duration={durationInSeconds} />}
      <StyledContainer animation={animation} data-testid={`workshop-modal-${type}`}>
        {children}
      </StyledContainer>
    </FadeOutContainer>
  );
};

AnimatedModal.propTypes = {
  withBackground: PropTypes.bool,
  children: PropTypes.any,
  shouldFadeOut: PropTypes.bool,
  type: PropTypes.string,
};

AnimatedModal.defaultProps = {
  children: null,
  shouldFadeOut: false,
  withBackground: true,
  type: '',
};

export default AnimatedModal;

const StyledContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  animation: ${({ animation }) => animation} forwards;
  
  @keyframes modalFromTop {
    0% {
      transform: translateY(-350px);
    }
    35% {
      transform: translateY(10px);
    }
    70% {
      transform: translateY(-5px);
    }
    100% {
      transform: translateY(0);
    }
  }

  @keyframes modalFromBottom {
    0%, 15% {
      transform: translateY(550px);
    }
    35% {
      transform: translateY(-10px);
    }
    70% {
      transform: translateY(5px);
    }
    100% {
      transform: translateY(0);
    }
  }

  @keyframes notificationFromBottom {
    0% {
      transform: translateY(350px);
      opacity: 0;
    }
    35% {
      transform: translateY(-10px);
      opacity: 0.35;
    }
    85% {
      transform: translateY(5px);
      opacity: 0.7;
    }
    100% {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @keyframes smoothPop {
    0% {
      transform: scale(0);
    }
    100% {
      transform: scale(1);
    }
  }

  @keyframes popY {
    0% {
      transform: translateY(0) scaleX(0.5) scaleY(0);
    }
    70% {
      transform: translateY(-10px) scaleX(1.05) scaleY(1.05);
    }
    100% {
      transform: translateY(0) scaleX(1) scaleY(1);
    }
  }
`;

const FadeOutContainer = styled.div`
  animation: ${({ shouldFadeOut }) => (shouldFadeOut ? 'fadeOut 2s forwards' : 'none')};
  position: absolute;
  top: 0;
  left:0;
  width: 1024px;
  height: 576px;
  transform: translateZ(0);
  perspective: 1000;
  backface-visibility: hidden;
  @keyframes fadeOut {
    0% { opacity: 1; }
    100% { opacity: 0; }
  }
`;

const fade = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 0.7;
  }
`;

const BackgroundOverlay = styled.div`
  animation: ${fade} ${({ duration }) => duration || '0.5s'} ease-out forwards;
  position: absolute;
  top: 0;
  left: 0;
  width: 1024px;
  height: 576px;
  background-color: #110a02;
  opacity: 0.1;
`;
